import { Link } from 'react-router';
import { ArticleLayout, H2, H3, P, UL, LI, Callout } from './ArticleLayout';

export function AiDlp() {
  return (
    <ArticleLayout
      currentPath="/ai-dlp"
      breadcrumb="AI DLP"
      eyebrow="Guide"
      title={
        <>
          AI Data Loss Prevention <span className="text-red-accent">(DLP)</span>: a complete guide
        </>
      }
      lead="Generative AI has become the fastest-growing way for sensitive data to leave an organisation. AI data loss prevention is the practice — and the tooling — that keeps PII, payment data, credentials and source code out of tools like ChatGPT, Copilot and Claude."
    >
      <H2 id="what-is-ai-dlp">What is AI DLP?</H2>
      <P>
        AI data loss prevention (AI DLP) is a category of security control that inspects the content
        employees send to generative AI services and blocks, warns on, or logs anything sensitive
        before it leaves the device. It is a response to a simple shift in behaviour: people now paste
        spreadsheets, customer records, contracts and proprietary code straight into an AI chatbot to
        get work done faster.
      </P>
      <P>
        Traditional data loss prevention watches email, USB drives and file uploads. AI DLP watches the
        new exfiltration channel — the prompt. The moment an employee types or pastes into ChatGPT, or
        an IDE assistant sends a code file to a model, that data is leaving your control. AI DLP
        inspects that content in real time and applies policy.
      </P>

      <H2 id="why-it-matters">Why AI DLP matters now</H2>
      <P>
        Adoption ran ahead of governance. Surveys through 2025 consistently found that a large share of
        employees paste sensitive work data into AI tools, often through personal accounts their
        employer cannot see. That behaviour is called{' '}
        <Link to="/shadow-ai" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
          shadow AI
        </Link>
        , and it is the single biggest driver of AI data leakage.
      </P>
      <UL>
        <LI>
          <strong className="text-text-primary">The data never comes back.</strong> Once a prompt
          reaches a third-party model, you cannot recall it. It may be logged, cached, or used to train
          future models depending on the provider and plan.
        </LI>
        <LI>
          <strong className="text-text-primary">It is invisible to legacy tooling.</strong> Prompts
          go out over ordinary HTTPS to trusted domains, so email and network DLP never see them.
        </LI>
        <LI>
          <strong className="text-text-primary">The regulatory exposure is real.</strong> Leaking
          personal data into an AI tool can be a reportable breach under GDPR and the UK DPA 2018.
        </LI>
      </UL>

      <H2 id="what-it-detects">What AI DLP should detect</H2>
      <P>
        Effective AI DLP classifies content by category and lets you set a different action for each.
        The categories that matter most in practice are:
      </P>
      <UL>
        <LI>
          <strong className="text-text-primary">PII</strong> — names, emails, phone numbers, addresses
          and national identifiers such as NI or Social Security numbers.
        </LI>
        <LI>
          <strong className="text-text-primary">PCI / payment data</strong> — card numbers, bank
          accounts and sort codes.
        </LI>
        <LI>
          <strong className="text-text-primary">Credentials and secrets</strong> — API keys, tokens,
          passwords and connection strings.
        </LI>
        <LI>
          <strong className="text-text-primary">Confidential IP</strong> — source code, internal
          documents and trade secrets.
        </LI>
      </UL>

      <H2 id="how-it-works">How AI DLP works</H2>
      <P>
        There are two broad architectures. A network gateway approach routes AI traffic off the device
        to a cloud service that inspects requests. It is easy to centralise but breaks on desktop apps,
        blind to anything that bypasses the gateway, adds latency, and means your prompt content is
        transmitted somewhere else to be scanned. An endpoint approach inspects content at the
        operating-system level on the device itself, before it reaches an AI service — covering
        browsers, desktop apps and IDEs, with no browser extension to install per-app.
      </P>
      <P>
        Redbax takes the endpoint approach. The agent inspects AI traffic through a proxy that runs
        locally on the device — not a gateway in the cloud — so the content being classified never
        leaves the machine, and policy is applied in single-digit milliseconds. For a deeper
        comparison, see{' '}
        <Link
          to="/ai-dlp-vs-traditional-dlp"
          className="text-red-accent underline underline-offset-4 hover:text-red-hover"
        >
          AI DLP vs traditional DLP
        </Link>
        .
      </P>

      <H3>Block, warn, or log</H3>
      <P>
        Good AI DLP is not a blunt ban. Blanket blocking pushes employees to personal devices and makes
        the problem invisible again. The better model is graduated policy: block the highest-risk
        categories, warn on medium risk so the user can make a judgement call, and log everything for
        visibility. That keeps AI useful while closing the leak.
      </P>

      <Callout title="Practical starting point">
        <P>
          Start by turning detection on in log-only mode for two weeks. You will almost always discover
          more AI usage — and more sensitive data flowing to it — than expected. Use that evidence to
          set proportionate block/warn policies. Our{' '}
          <Link
            to="/stop-chatgpt-data-leaks"
            className="text-red-accent underline underline-offset-4 hover:text-red-hover"
          >
            guide to stopping ChatGPT data leaks
          </Link>{' '}
          walks through the full rollout.
        </P>
      </Callout>

      <H2 id="checklist">AI DLP buyer&apos;s checklist</H2>
      <UL>
        <LI>Inspects prompts and file uploads across desktop apps and IDEs — not just the browser.</LI>
        <LI>Classifies PII, PCI, credentials and source code with low false positives.</LI>
        <LI>Runs detection locally so content is not sent elsewhere to be scanned.</LI>
        <LI>Supports per-category block / warn / log policies by team or role.</LI>
        <LI>Gives the SOC a real-time view of what is being shared, storing metadata only.</LI>
        <LI>Meets GDPR / UK DPA requirements, with data stored in the UK.</LI>
      </UL>
      <P>
        Redbax is built to this checklist. See the{' '}
        <Link to="/features" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
          full feature set
        </Link>{' '}
        or{' '}
        <Link to="/contact" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
          register your interest
        </Link>
        .
      </P>
    </ArticleLayout>
  );
}
